import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { getProductAnalytics } from '../../../services/analyticsApi';

ChartJS.register(ArcElement, Tooltip, Legend);

const ProductsMetricsChart = () => {
  const [selectedPeriod, setSelectedPeriod] = React.useState('This Month');
  const [statusCounts, setStatusCounts] = React.useState({
    active: 0,
    low_stock: 0,
    out_of_stock: 0,
  });

  const periods = ['This Week', 'This Month', 'This Quarter'];

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getProductAnalytics();
        const list = Array.isArray(res?.productsByStatus)
          ? res.productsByStatus
          : [];
        const map = { active: 0, low_stock: 0, out_of_stock: 0 };
        for (const s of list) {
          const k = (s._id || s.status || '').toLowerCase();
          if (k.includes('low')) map.low_stock += s.count || 0;
          else if (k.includes('out')) map.out_of_stock += s.count || 0;
          else map.active += s.count || 0;
        }
        if (mounted) setStatusCounts(map);
      } catch (_) {
        if (mounted)
          setStatusCounts({ active: 82, low_stock: 12, out_of_stock: 6 });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedPeriod]);

  const total = Math.max(
    1,
    statusCounts.active + statusCounts.low_stock + statusCounts.out_of_stock
  );
  const currentData = [
    Math.round((statusCounts.active / total) * 100),
    Math.round((statusCounts.low_stock / total) * 100),
    Math.round((statusCounts.out_of_stock / total) * 100),
  ];

  const data = {
    labels: ['Active', 'Low Stock', 'Out of Stock'],
    datasets: [
      {
        data: currentData,
        backgroundColor: ['#00C95C', '#F6A303', '#EE4343'],
        borderColor: ['#00C95C', '#F6A303', '#EE4343'],
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll use our custom legend
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed}%`;
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
    },
    interaction: {
      intersect: false,
    },
  };

  return (
    <div
      className="rounded-[16px] p-4 sm:p-7 bg-white"
      style={{ boxShadow: '0px 4px 16px 0px #0000001A' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-[#373737] poppins text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px] tracking-[-0.64px] font-semibold">
          Products Metrics
        </h2>
        <div>
          <label className="sr-only" htmlFor="period">
            Period
          </label>
          <select
            id="period"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="poppins text-[12px] sm:text-[14px] leading-[18px] sm:leading-[20px] rounded-[10px] border border-[#E7E9EF] px-3 sm:px-4 py-2 text-[#111827] bg-white shadow-sm"
          >
            {periods.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
        {/* Pie Chart */}
        <div className="w-48 h-48 sm:w-60 sm:h-60 relative">
          <Pie data={data} options={options} />
        </div>

        {/* Legend */}
        <div className="flex md:flex-col gap-3 px-0 lg:px-20">
          {/* Reorder legend to match design: Low Stock, Out of Stock, Active */}
          {[
            { label: 'Low Stock', color: '#F6A303' },
            { label: 'Out of Stock', color: '#EE4343' },
            { label: 'Active', color: '#00C95C' },
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-[#636363] text-[12px] leading-[18px] poppins tracking-[-0.64px]">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductsMetricsChart;
